// src/pages/Faq/FaqPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  ShieldCheck, 
  CreditCard, 
  User, 
  Rocket, 
  ChevronDown, 
  X,
  Truck,
  Medal,
  Headphones,
  MessageCircle
} from 'lucide-react';
// ✅ استفاده از فایل faqsData.js که ارسال کردید
import { faqsData, categoriesData, featuresData } from '../../data/faqsData';

// ===== آیکون‌های دسته‌بندی =====
const categoryIcons = {
  ShieldCheck: ShieldCheck,
  CreditCard: CreditCard,
  User: User,
  Rocket: Rocket,
};

// ===== کامپوننت کارت ویژگی‌ها =====
const FeatureCard = ({ title, desc, icon: Icon, gradient }) => (
  <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mx-auto mb-4 text-white shadow-lg`}>
      <Icon className="w-7 h-7" />
    </div>
    <h3 className="font-bold text-gray-800 text-sm mb-1">{title}</h3>
    <p className="text-xs text-gray-400">{desc}</p>
  </div>
);

// ===== کامپوننت سوال =====
const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-gray-100 last:border-0">
    <button
      onClick={() => toggle(faq.id)}
      className="w-full flex items-center justify-between py-4 px-2 text-right transition-all hover:bg-gray-50/50 rounded-lg"
    >
      <span className="text-sm font-medium text-gray-800 flex-1">{faq.question}</span>
      <span className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
        <ChevronDown className="w-5 h-5" />
      </span>
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
      <p className="text-sm text-gray-500 leading-relaxed pb-4 px-2">{faq.answer}</p>
    </div>
  </div>
);

// ===== صفحه اصلی FAQ =====
const FaqPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState([]);

  // ===== فیلتر سوالات بر اساس دسته و جستجو =====
  const filteredFAQs = faqsData.filter(faq => {
    const matchCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchSearch = faq.question.includes(searchQuery) || faq.answer.includes(searchQuery);
    return matchCategory && matchSearch;
  });

  // ===== تاگل باز/بسته شدن سوال =====
  const toggleItem = (id) => {
    setOpenItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // ===== پاک کردن جستجو =====
  const clearSearch = () => setSearchQuery('');

  // ===== دریافت اطلاعات دسته =====
  const getCategoryInfo = (id) => categoriesData.find(c => c.id === id);

  // ===== دریافت آیکون دسته =====
  const getCategoryIcon = (id) => {
    const category = getCategoryInfo(id);
    if (!category) return User;
    const IconComponent = categoryIcons[category.icon];
    return IconComponent || User;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50" dir="rtl">
      
      {/* ===== هدر قهرمانی ===== */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#8A9A7B] via-[#9EAD8C] to-[#6B7D5E]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse delay-700" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <MessageCircle className="w-4 h-4 text-white/80" />
              <span className="text-white/90 text-xs font-medium">پشتیبانی ۲۴/۷</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              سوالات متداول
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto">
              پاسخ سوالات خود را در اینجا پیدا کنید. اگر سوالی دارید که در لیست نیست، با پشتیبانی تماس بگیرید.
            </p>

            {/* ===== جستجو ===== */}
            <div className="relative mt-6 max-w-lg mx-auto">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="جستجو در سوالات..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-12 pl-12 py-3 rounded-2xl border-0 shadow-lg text-sm focus:ring-2 focus:ring-white/50 outline-none bg-white/95 backdrop-blur-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ===== بخش ویژگی‌ها ===== */}
      <div className="container mx-auto px-4 -mt-6 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {featuresData.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>

      {/* ===== محتوای اصلی ===== */}
      <div className="container mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* ===== سایدبار دسته‌بندی ===== */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <h3 className="font-bold text-gray-800 text-sm mb-3 px-2">دسته‌بندی</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`w-full text-right px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeCategory === 'all'
                      ? 'bg-[#8A9A7B] text-white shadow-md shadow-[#8A9A7B]/20'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  همه سوالات
                  <span className="mr-2 text-xs opacity-60">({faqsData.length})</span>
                </button>

                {categoriesData.map((category) => {
                  const Icon = categoryIcons[category.icon] || User;
                  const count = faqsData.filter(f => f.category === category.id).length;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full text-right px-3 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        activeCategory === category.id
                          ? 'bg-[#8A9A7B] text-white shadow-md shadow-[#8A9A7B]/20'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="flex-1">{category.title}</span>
                      <span className="text-xs opacity-60">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* ===== لیست سوالات ===== */}
          <main className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6">
              {/* تعداد نتایج */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-800">
                  {filteredFAQs.length} سوال
                  {activeCategory !== 'all' && (
                    <span className="text-sm font-normal text-gray-400 mr-2">
                      در دسته {getCategoryInfo(activeCategory)?.title || ''}
                    </span>
                  )}
                </h2>
                {searchQuery && (
                  <span className="text-xs text-gray-400">نتایج جستجو</span>
                )}
              </div>

              {/* نمایش سوالات */}
              {filteredFAQs.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {filteredFAQs.map((faq) => {
                    const isOpen = openItems.includes(faq.id);
                    const category = getCategoryInfo(faq.category);
                    const Icon = category ? categoryIcons[category.icon] : User;
                    return (
                      <div key={faq.id} className="py-1">
                        <div className="flex items-start gap-3">
                          {category && (
                            <div className={`w-8 h-8 rounded-full ${category.bg} flex items-center justify-center flex-shrink-0 mt-3`}>
                              <Icon className="w-4 h-4 text-[#8A9A7B]" />
                            </div>
                          )}
                          <div className="flex-1">
                            <FAQItem 
                              faq={faq} 
                              isOpen={isOpen} 
                              toggle={toggleItem} 
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">نتیجه‌ای یافت نشد</h3>
                  <p className="text-sm text-gray-400">عبارت جستجو را تغییر دهید یا دسته دیگری را انتخاب کنید.</p>
                  <button
                    onClick={() => {
                      setActiveCategory('all');
                      setSearchQuery('');
                    }}
                    className="mt-4 px-6 py-2 bg-[#8A9A7B] text-white rounded-xl text-sm font-medium hover:bg-[#6B7D5E] transition-colors"
                  >
                    حذف همه فیلترها
                  </button>
                </div>
              )}
            </div>

            {/* ===== بخش تماس با پشتیبانی ===== */}
            <div className="mt-8 bg-gradient-to-br from-[#8A9A7B] to-[#6B7D5E] rounded-2xl p-6 md:p-8 text-center text-white">
              <h3 className="text-xl font-bold mb-2">هنوز سوال دارید؟</h3>
              <p className="text-white/80 text-sm max-w-md mx-auto">
                تیم پشتیبانی ما آماده پاسخگویی به شماست. ۲۴ ساعته، ۷ روز هفته در خدمت شما هستیم.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="mt-4 bg-white text-[#8A9A7B] px-8 py-3 rounded-xl font-bold hover:bg-gray-50 transition-colors shadow-lg"
              >
                تماس با پشتیبانی
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default FaqPage;